import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import CustomButton from '../../../components/CustomButton';
import blogimg from '../../../assets/images/blogimg01.png';

const blogs = [
  {
    title: 'No Esperes el Problema: La Importancia de la Asesoría Legal Preventiva',
    author: 'Emilia Montes',
    date: '2024-06-01',
    readTime: '3 min',
    description:
      '¿Sabías que muchos conflictos se pueden evitar con el consejo legal adecuado a tiempo? Descubre cómo la asesoría legal general te brinda la tranquilidad de tomar decisiones informadas en tu vida personal y profesional, protegiéndote antes de que surjan los problemas.',
    image: blogimg,
  },
  {
    title: 'Más Allá del Juicio: Estrategias para la Mediación Obligatoria',
    author: 'Emi Montes',
    date: '2024-05-20',
    readTime: '2 min',
    description:
      'Una buena mediación evita problemas judiciales innecesarios. Aprende cómo entender y aplicar esta estrategia puede cambiar tu situación legal.',
    image: blogimg,
  },
  {
    title: 'Derechos Laborales: Todo lo que Necesitas Saber',
    author: 'Carlos López',
    date: '2024-05-15',
    readTime: '4 min',
    description:
      'Conoce tus derechos laborales fundamentales y cómo protegerte en el ámbito profesional. Una guía completa para empleados y empleadores.',
    image: blogimg,
  },
  {
    title: 'Contratos Digitales: El Futuro Legal',
    author: 'Ana García',
    date: '2024-05-10',
    readTime: '5 min',
    description:
      'La era digital ha transformado la forma en que hacemos contratos. Descubre las nuevas tendencias y regulaciones en el mundo digital.',
    image: blogimg,
  },
];

const BlogCard = ({ blog, index }) => (
  <motion.div
    className="relative w-72 sm:w-80 max-w-sm rounded-2xl overflow-hidden group cursor-pointer flex-shrink-0 shadow-lg hover:shadow-xl transition-shadow duration-300"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.4, delay: index * 0.1 }}
    whileHover={{ y: -4, transition: { duration: 0.2 } }}
  >
    <img src={blog.image} alt={blog.title} className="w-full h-96 sm:h-64 md:h-72 object-cover" />
    <div className="absolute inset-0 bg-black bg-opacity-40" />
    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-3 sm:p-4 md:p-6 flex flex-col justify-between">
      {/* Header - Título en la parte superior */}
      <div className="pt-2">
        <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white leading-tight drop-shadow-xl">
          {blog.title}
        </h3>
      </div>

      {/* Footer - Metadatos en la parte inferior */}
      <div className="mb-2 sm:mb-3">
        <span className="text-sm sm:text-base text-white font-medium drop-shadow-lg">
          {blog.author} •{' '}
          {new Date(blog.date).toLocaleDateString('es-MX', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          })}{' '}
          • {blog.readTime}
        </span>
      </div>
    </div>
  </motion.div>
);

const BlogCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  const containerRef = useRef(null);

  // Escuchar cambios en el tamaño de ventana
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setCurrentIndex(0); // Reset al redimensionar
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  // Calcular el ancho de cada item basado en el viewport actual
  const getItemWidth = () => {
    // w-72 = 288px (mobile), w-80 = 320px (sm+), gap-6 = 24px
    const cardWidth = windowWidth >= 640 ? 320 : 288;
    const gap = 24;
    return cardWidth + gap;
  };

  const currentItemWidth = getItemWidth();

  // Calcular cuántas cards caben en el viewport
  const cardsVisible = Math.floor(windowWidth / currentItemWidth);
  const maxIndex = Math.max(0, blogs.length - cardsVisible);

  const scrollToNext = () => {
    if (currentIndex < maxIndex) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const scrollToPrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const scrollToIndex = index => {
    setCurrentIndex(Math.min(Math.max(0, index), maxIndex));
  };

  return (
    <div className="relative py-16 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header - Apple Style */}
        <div className="flex items-center justify-between mb-12">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 tracking-tight">
              Publicaciones
            </h2>
          </motion.div>

          <motion.div
            className="flex items-center space-x-4"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <button
              onClick={scrollToPrev}
              disabled={currentIndex === 0}
              className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-200 ${
                currentIndex === 0
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-900 text-white hover:bg-gray-800 hover:scale-105'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            <button
              onClick={scrollToNext}
              disabled={currentIndex >= maxIndex}
              className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-200 ${
                currentIndex >= maxIndex
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-900 text-white hover:bg-gray-800 hover:scale-105'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </motion.div>
        </div>

        {/* Cards Container */}
        <div className="relative overflow-hidden">
          <motion.div
            ref={containerRef}
            className="flex gap-6 transition-transform duration-500 ease-out"
            style={{
              transform: `translateX(-${currentIndex * currentItemWidth}px)`,
            }}
          >
            {blogs.map((blog, index) => (
              <BlogCard key={index} blog={blog} index={index} />
            ))}
          </motion.div>
        </div>

        {/* Dots Indicator - Apple Style */}
        <div className="flex justify-center mt-8 space-x-2">
          {Array.from({ length: maxIndex + 1 }, (_, index) => (
            <button
              key={index}
              onClick={() => scrollToIndex(index)}
              className={`w-2 h-2 rounded-full transition-all duration-200 ${
                index === currentIndex ? 'bg-gray-900 scale-125' : 'bg-gray-300 hover:bg-gray-400'
              }`}
            />
          ))}
        </div>

        <motion.div
          className="flex items-center justify-center mt-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <CustomButton text="Ver Todas las Publicaciones" />
        </motion.div>
      </div>
    </div>
  );
};

export default BlogCarousel;
